import * as React from "react";

import {
  ConvertToWithDataSourceProps,
  ExtendedPickerProps
} from "./ExtendedPicker.types.ts";

export const withStaticItems = <
  P extends Pick<
    ExtendedPickerProps<TSuggestionItem, TItem>,
    | "onFetchItems"
    | "onSearchItems"
    | "onRenderItem"
    | "keyAccessor"
    | "onEmptySearchItems"
    | "itemLimit"
    | "ignoreWhitespaceOnly"
    | "excludeSelectedItems"
    | "refetchDataSource"
  >,
  TSuggestionItem,
  TItem = TSuggestionItem
>(
  Component: React.FunctionComponent<P>
): React.ComponentType<ConvertToWithDataSourceProps<P, TItem>> => {
  type InnerProps = ConvertToWithDataSourceProps<P, TItem>;
  type InnerState = {
    items?: TItem[];
  };

  class WithStaticItemsPickerComponent extends React.Component<
    InnerProps,
    InnerState
  > {
    private datasourcePromise?: PromiseLike<TItem[]>;

    constructor(props: InnerProps) {
      super(props);
      this.state = {};
    }

    static defaultProps: Partial<InnerProps> = {
      searchLimit: 20,
      searchMoreLimit: 100,
      ignoreWhitespaceOnly: false,
      excludeSelectedItems: false
    } as Partial<InnerProps>;

    static readonly displayName = `${
      Component.displayName || (Component as any).name
    }`;

    private _getDataSource = async () => {
      if (!this.datasourcePromise || this.props.refetchDataSource) {
        this.datasourcePromise = Promise.resolve(this.props.fetchDataSource());
      }

      const items = await this.datasourcePromise;
      if (!this.state.items || this.props.refetchDataSource) {
        this.setState({ items });
      }
      return items;
    };

    private _onSearchItems: ExtendedPickerProps<
      TSuggestionItem,
      TItem
    >["onSearchItems"] = async (filter, previousResult) => {
      const items = await this._getDataSource();
      const take = previousResult
        ? this.props.searchMoreLimit!
        : this.props.searchLimit!;

      const filteredItems = await Promise.resolve(
        this.props.onFilterItems(filter, { take, items })
      );
      return {
        results: filteredItems.slice(0, take),
        take,
        skip: 0,
        total: filteredItems.length
      };
    };

    private _onFetchItems: ExtendedPickerProps<
      TSuggestionItem,
      TItem
    >["onFetchItems"] = async keys => {
      const items = await this._getDataSource();
      return items.filter(x => keys.includes(this.props.keyAccessor(x)));
    };

    render() {
      const {
        itemLimit = this.state.items?.length,
        onEmptySearchItems = () => this._getDataSource(),
        ...props
      } = this.props;

      return (
        <Component
          itemLimit={itemLimit}
          {...(props as unknown as P)}
          onFetchItems={this._onFetchItems}
          onSearchItems={this._onSearchItems}
          onEmptySearchItems={onEmptySearchItems}
        />
      );
    }
  }

  return WithStaticItemsPickerComponent;
};
